import { Injectable } from '@angular/core';
import { supabase } from '../supabase/supabase.client';
import { EncryptionService } from './encryption.service';
import { SettingsService } from './settings.service';

export interface ContactMessage {
  name: string;
  email: string;
  country_code: string;
  phone: string;
  subject: string;
  message: string;
}

export interface ContactMessageRecord extends ContactMessage {
  id: string;
  created_at: string;
  updated_at: string;
  is_read: boolean;
  is_answered: boolean;
  admin_notes?: string;
}

@Injectable({
  providedIn: 'root'
})
export class ContactService {
  constructor(
    private encryptionService: EncryptionService,
    private settingsService: SettingsService
  ) {}

  // Get contact information from settings
  getPhoneNumber(): string {
    return this.settingsService.getSetting('contact_phone', '+96176316924');
  }

  getEmail(): string {
    return this.settingsService.getSetting('contact_email', 'immovea.realestate@gmail.com');
  }

  getWhatsAppNumber(): string {
    return this.settingsService.getSetting('contact_whatsapp', '96176316924');
  }

  getFormattedWhatsAppNumber(): string {
    const number = this.getWhatsAppNumber();
    // Format as +961 76 316 924 (Lebanon format)
    if (number.startsWith('961') && number.length === 11) {
      return `+961 ${number.substring(3, 5)} ${number.substring(5, 8)} ${number.substring(8)}`;
    }
    // Default: add + prefix if not present
    return number.startsWith('+') ? number : `+${number}`;
  }

  private async getAdminEmails(): Promise<string[]> {
    // Ensure settings are loaded
    await this.settingsService.loadSettings();
    return this.settingsService.getSetting('admin_emails');
  }

  // Generate WhatsApp link with optional custom message
  getWhatsAppLink(message?: string): string {
    const defaultMessage = 'Hi, I would like to inquire about your services.';
    const text = encodeURIComponent(message || defaultMessage);
    // Remove all non-digit characters (spaces, +, -, etc.) to ensure clean WhatsApp URL
    const whatsappNumber = this.getWhatsAppNumber().replace(/\D/g, '');
    return `https://wa.me/${whatsappNumber}?text=${text}`;
  }

  // Generate WhatsApp link for a specific property
  getPropertyWhatsAppLink(propertyTitle: string, propertyUrl?: string): string {
    let message = `Hi, I am interested in: ${propertyTitle}`;
    if (propertyUrl) {
      message += `\n\nProperty Link: ${propertyUrl}`;
    }
    return this.getWhatsAppLink(message);
  }

  async submitContactForm(data: ContactMessage): Promise<ContactMessageRecord> {
    try {
      // Encrypt sensitive fields before storing
      const encryptedData = this.encryptionService.encryptObject(data, ['email', 'phone', 'message']);

      // Insert the contact message into the database
      // Note: We don't use .select() for anonymous users as it requires SELECT permission
      const { data: insertedData, error } = await supabase
        .from('contact_messages')
        .insert([{
          name: encryptedData.name,
          email: encryptedData.email,
          country_code: encryptedData.country_code,
          phone: encryptedData.phone,
          subject: encryptedData.subject,
          message: encryptedData.message
        }]);

      if (error) {
        throw new Error(error.message || 'Failed to submit contact form');
      }

      // Send email notification to admin (non-blocking, don't wait)
      this.sendAdminNotificationEmail(data).catch(err => {
        // Log but don't throw
      });

      // Send confirmation email to user (non-blocking, don't wait)
      this.sendUserConfirmationEmail(data).catch(err => {
        // Log but don't throw
      });

      // Return a mock record since we can't SELECT as anonymous user
      // The insert was successful if we got here
      const mockRecord: ContactMessageRecord = {
        id: 'pending', // We don't have the ID, but the insert succeeded
        ...data,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        is_read: false,
        is_answered: false
      };

      return mockRecord;
    } catch (error) {
      throw error;
    }
  }

  private async sendAdminNotificationEmail(data: ContactMessage): Promise<void> {
    try {
      const adminEmails = await this.getAdminEmails();
      
      // Call Supabase Edge Function to send email to all admins
      const { data: responseData, error } = await supabase.functions.invoke('send-contact-email', {
        body: {
          recipientEmail: adminEmails, // Send to multiple admins
          recipientName: 'Admin',
          visitorName: data.name,
          visitorEmail: data.email,
          visitorPhone: `${data.country_code} ${data.phone}`,
          subject: data.subject,
          message: data.message,
          emailType: 'admin'
        }
      });

      if (error) {
        // Don't throw - email errors shouldn't fail the form submission
        console.error('Failed to send admin notification email:', error);
      } else if (responseData?.failedCount > 0) {
        console.warn('Some emails failed to send:', responseData.results?.filter((r: any) => !r.success));
      }
    } catch (error) {
      // Don't throw - email errors shouldn't fail the form submission
      console.error('Error sending admin notification email:', error);
    }
  }

  private async sendUserConfirmationEmail(data: ContactMessage): Promise<void> {
    try {
      // Call Supabase Edge Function to send confirmation email to user
      const { error } = await supabase.functions.invoke('send-contact-email', {
        body: {
          recipientEmail: data.email,
          recipientName: data.name,
          subject: data.subject,
          message: data.message,
          emailType: 'confirmation'
        }
      });

      if (error) {
        // Don't throw - email errors shouldn't fail the form submission
      }
    } catch (error) {
      // Don't throw - email errors shouldn't fail the form submission
    }
  }

  async getContactMessages(): Promise<ContactMessageRecord[]> {
    try {
      const { data, error } = await supabase
        .from('contact_messages')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        throw new Error(error.message || 'Failed to fetch contact messages');
      }

      return data as ContactMessageRecord[];
    } catch (error) {
      throw error;
    }
  }

  async markAsRead(id: string): Promise<void> {
    try {
      // First, get the current state
      const { data: message, error: fetchError } = await supabase
        .from('contact_messages')
        .select('is_read')
        .eq('id', id)
        .single();

      if (fetchError) {
        throw new Error(fetchError.message || 'Failed to fetch message');
      }

      // Toggle the is_read status
      const newReadStatus = !message.is_read;

      const { error } = await supabase
        .from('contact_messages')
        .update({ is_read: newReadStatus })
        .eq('id', id);

      if (error) {
        throw new Error(error.message || 'Failed to mark message as read');
      }
    } catch (error) {
      throw error;
    }
  }

  async markAsAnswered(id: string): Promise<void> {
    try {
      // First, get the current state
      const { data: message, error: fetchError } = await supabase
        .from('contact_messages')
        .select('is_answered')
        .eq('id', id)
        .single();

      if (fetchError) {
        throw new Error(fetchError.message || 'Failed to fetch message');
      }

      // Toggle the is_answered status
      const newAnsweredStatus = !message.is_answered;

      const { error } = await supabase
        .from('contact_messages')
        .update({ is_answered: newAnsweredStatus })
        .eq('id', id);

      if (error) {
        throw new Error(error.message || 'Failed to mark message as answered');
      }
    } catch (error) {
      throw error;
    }
  }

  async updateAdminNotes(id: string, notes: string): Promise<void> {
    try {
      // Encrypt admin notes before storing
      const encryptedNotes = notes ? this.encryptionService.encrypt(notes) : null;
      
      const { error } = await supabase
        .from('contact_messages')
        .update({ admin_notes: encryptedNotes })
        .eq('id', id);

      if (error) {
        throw new Error(error.message || 'Failed to update admin notes');
      }
    } catch (error) {
      throw error;
    }
  }

  async deleteMessage(id: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('contact_messages')
        .delete()
        .eq('id', id);

      if (error) {
        throw new Error(error.message || 'Failed to delete message');
      }
    } catch (error) {
      throw error;
    }
  }
}
